import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { useUser } from "@clerk/clerk-react";
import { FaEllipsisV, FaEye, FaPen, FaPlus, FaSearch, FaTrash, FaUserShield } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { useAppState } from "~/hooks/useAppState";
import { useGroupMembersMutations } from "~/hooks/useGroupMembersMutations";
import { GroupMemberWithRole, useUserHomeMembers } from "~/hooks/useUserHomeMembers";
import { Member, useAppStore } from "~/store";

// Helper function to calculate age from birthdate
export const calculateAge = (birthdate: string | undefined): number => {
  if (!birthdate) return 0;
  const today = new Date();
  const birthDate = new Date(birthdate);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Simple fuzzy search function
const fuzzySearch = (items: Member[], searchTerm: string): Member[] => {
  if (!searchTerm) return items; // Return all when no search term

  const lowerSearchTerm = searchTerm.toLowerCase();

  // Score function - higher is better match
  const getScore = (item: Member): number => {
    const name = item.name.toLowerCase();

    // Exact match gets highest score
    if (name === lowerSearchTerm) return 100;

    // Starts with search term gets high score
    if (name.startsWith(lowerSearchTerm)) return 75;

    // Contains search term gets medium score
    if (name.includes(lowerSearchTerm)) return 50;

    // Check for partial matches (each word)
    const nameParts = name.split(" ");
    for (const part of nameParts) {
      if (part.startsWith(lowerSearchTerm)) return 40;
      if (part.includes(lowerSearchTerm)) return 30;
    }

    // Check for character by character fuzzy match
    let matchCount = 0;
    let lastIndex = -1;

    for (const char of lowerSearchTerm) {
      const index = name.indexOf(char, lastIndex + 1);
      if (index > -1) {
        matchCount++;
        lastIndex = index;
      }
    }

    // Calculate percentage of search term that matched
    const matchPercentage = matchCount / lowerSearchTerm.length;
    if (matchPercentage > 0.7) return 20;
    if (matchPercentage > 0.5) return 10;

    return 0;
  };

  // Score each item and filter out non-matches
  return items
    .map((item) => ({ item, score: getScore(item) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
};

interface MembersCardProps {
  mode?: "userMember" | "otherMembers" | "default";
  onSkip?: () => void;
}

const MembersCard: React.FC<MembersCardProps> = ({ mode = "default", onSkip }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [members, setMembers] = useState<Member[]>([]);
  const [groupMembers, setGroupMembers] = useState<GroupMemberWithRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [noDefaultGroup, setNoDefaultGroup] = useState(false);
  const [highlightedMemberId, setHighlightedMemberId] = useState<number | null>(null);
  const [confirmationText, setConfirmationText] = useState("");
  const [isConfirmationValid, setIsConfirmationValid] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const setCurrentMember = useAppStore((state) => state.setCurrentMember);
  const setEditMode = useAppStore((state) => state.setEditMode);
  const debugMode = useAppStore((state) => state.debugMode);
  const { userInfo } = useAppState("userInfo");
  const { isSignedIn, isLoaded } = useUser();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmInputRef = useRef<HTMLInputElement>(null);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);

  // Use our new hook instead of the React Query hook
  const { data: userHomeGroupMembers, isLoading, error: queryError, defaultGroup } = useUserHomeMembers();
  const { deleteMemberMutation, toggleAdminStatusMutation } = useGroupMembersMutations();

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const inputBg = useColorModeValue("white", "gray.700");
  const addButtonColor = useColorModeValue("green.500", "green.300");
  const scrollBg = useColorModeValue("gray.100", "gray.700");
  const scrollThumbBg = useColorModeValue("gray.400", "gray.500");

  // Add a strong highlight color for the selected row
  const highlightBg = useColorModeValue("blue.50", "blue.900");
  const highlightBorder = useColorModeValue("blue.200", "blue.700");
  const errorTextColor = useColorModeValue("red.600", "red.300");

  // Update state when userHomeGroupMembers data changes
  useEffect(() => {
    if (userHomeGroupMembers) {
      setGroupMembers(userHomeGroupMembers);

      // Extract just the member objects for filtering and display
      const extractedMembers = userHomeGroupMembers.map((groupMember) => groupMember.member);
      setMembers(extractedMembers);

      // Set noDefaultGroup based on defaultGroup from hook
      setNoDefaultGroup(!defaultGroup);
    }

    if (queryError) {
      setError(queryError ? String(queryError) : "An error occurred");
    }
  }, [userHomeGroupMembers, queryError, defaultGroup]);

  // Memoize filtered members to avoid recalculation on every render
  const filteredMembers = useMemo(() => {
    return fuzzySearch(members, searchTerm);
  }, [members, searchTerm]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle member selection for editing
  const handleEditMember = (member: Member) => {
    // Check if user is signed in
    if (!isSignedIn) {
      // Show toast notification to sign up or log in
      toast({
        title: "Authentication required",
        description: "Please sign up or log in to manage members.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top"
      });
      return;
    }

    // Only allow editing if no modal is open
    if (!isOpen) {
      setCurrentMember(member);
      setEditMode(true);
      navigate("/members");
    }
  };

  // Handle adding a new member
  const handleAddMember = () => {
    // Check if user is signed in
    if (!isSignedIn) {
      // Show toast notification to sign up or log in
      toast({
        title: "Authentication required",
        description: "Please sign up or log in to manage members.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top"
      });
      return;
    }

    // Clear any existing member first
    setCurrentMember(undefined);

    // Then create a new empty member with only default values
    setTimeout(() => {
      setCurrentMember({
        id: 0, // This will be replaced with a real ID when saved
        name: "",
        birthdate: "",
        gender: "F", // Default gender (Female)
        email: ""
      });
      setEditMode(true); // Enable edit mode for the new member
      navigate("/members"); // Navigate to the Members page
    }, 10); // Small timeout to ensure state is updated properly
  };

  // Handle opening the delete confirmation dialog
  const handleDeleteClick = (member: Member, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent onClick events

    // Check if user is signed in
    if (!isSignedIn) {
      // Show toast notification to sign up or log in
      toast({
        title: "Authentication required",
        description: "Please sign up or log in to manage members.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top"
      });
      return;
    }

    setHighlightedMemberId(member.id); // Set highlighted row
    setMemberToDelete(member);
    setConfirmationText(""); // Clear any previous confirmation text
    setIsConfirmationValid(false); // Reset validation
    onOpen();
    // Focus the confirmation input after the modal opens
    setTimeout(() => {
      if (confirmInputRef.current) {
        confirmInputRef.current.focus();
      }
    }, 100);
  };

  // Update handleConfirmDelete to use the mutation
  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const result = await deleteMemberMutation.mutateAsync(memberToDelete.id);

      if (result.success) {
        // Close the dialog and show success message
        onClose();
        toast({
          title: "Member deleted",
          description: `${memberToDelete.name} has been removed.`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top"
        });

        // No need to manually update the UI or refetch - React Query will handle cache invalidation
      } else {
        // Show error message
        setDeleteError(result.error || "Failed to delete member");
      }
    } catch (err) {
      console.error("Error deleting member:", err);
      setDeleteError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle closing the dialog
  const handleCloseDialog = () => {
    onClose();
    setMemberToDelete(null);
    setHighlightedMemberId(null);
    setConfirmationText("");
    setIsConfirmationValid(false);
  };

  // Handle confirmation text change
  const handleConfirmationTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmationText(value);
    // Validate if the text matches the member's name exactly
    setIsConfirmationValid(memberToDelete ? value === memberToDelete.name : false);
  };

  // Handle toggling admin status
  const handleToggleAdminStatus = async (member: Member, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent parent click events

    try {
      if (!defaultGroup) {
        toast({
          title: "Error",
          description: "No default group found for your account",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top"
        });
        return;
      }

      const groupId = defaultGroup.id;
      console.log(
        `Toggling admin status for member ${member.id} (${member.name}) in group ${groupId} (${defaultGroup.name})`
      );

      // Use the mutation hook to toggle admin status
      const result = await toggleAdminStatusMutation.mutateAsync({
        groupId,
        memberId: member.id
      });

      if (result.success) {
        // Show success message
        toast({
          title: result.message || "Member status updated",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top"
        });

        // No need to manually refresh - React Query will handle cache invalidation
      } else {
        // Show error message
        console.error("Error toggling admin status:", result.error);
        toast({
          title: "Error",
          description: result.error || "Failed to update member status",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top"
        });
      }
    } catch (error) {
      console.error("Error toggling admin status:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update member status",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top"
      });
    }
  };

  return (
    <>
      {/* Display debug information when debug mode is active */}
      {/*debugMode && <DebugStoreDisplay />*/}

      <Card
        bg={cardBg}
        borderColor={borderColor}
        borderWidth="1px"
        boxShadow="md"
        borderRadius="lg"
        width="100%"
        maxW={{ base: "100%", sm: "100%", md: "100%" }}
        alignSelf="flex-start"
        position="relative"
      >
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center" direction={{ base: "column", sm: "row" }} gap={2}>
            <Flex align="center">
              <Heading size="md">Members</Heading>
              <Tooltip label={isSignedIn ? "Add new member" : "Sign in to add members"} placement="top" hasArrow>
                <IconButton
                  icon={<FaPlus />}
                  aria-label="Add new member"
                  size="sm"
                  colorScheme="green"
                  variant={isSignedIn ? "ghost" : "outline"}
                  ml={2}
                  opacity={isSignedIn ? 1 : 0.6}
                  _hover={{ color: isSignedIn ? addButtonColor : undefined }}
                  onClick={handleAddMember}
                />
              </Tooltip>

              {/* Skip button for userMember mode */}
              {mode === "userMember" && onSkip && (
                <Tooltip label="I will not be tracking my own health metrics" placement="top" hasArrow maxW="300px">
                  <Button variant="outline" size="sm" colorScheme="gray" ml={2} onClick={onSkip}>
                    Skip
                  </Button>
                </Tooltip>
              )}
            </Flex>
            <InputGroup size="sm" maxW={{ base: "100%", sm: "180px", md: "220px" }}>
              <InputLeftElement pointerEvents="none">
                <FaSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder={isSignedIn ? "Search members..." : "Sign in to search"}
                value={searchTerm}
                onChange={handleSearchChange}
                bg={inputBg}
                borderRadius="md"
                isDisabled={!isSignedIn}
                opacity={isSignedIn ? 1 : 0.7}
              />
            </InputGroup>
          </Flex>
        </CardHeader>

        <CardBody>
          {/* Show a clear message when in userMember mode */}
          {mode === "userMember" && (
            <Box mb={4} p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
              <Text color="blue.700" fontSize="sm" fontWeight="medium">
                Tell us about yourself to get started. This information helps us personalize your experience.
              </Text>
            </Box>
          )}

          <Box
            maxH="400px"
            overflowY="auto"
            css={{
              "&::-webkit-scrollbar": {
                width: "6px"
              },
              "&::-webkit-scrollbar-track": {
                background: scrollBg,
                borderRadius: "4px"
              },
              "&::-webkit-scrollbar-thumb": {
                background: scrollThumbBg,
                borderRadius: "4px"
              },
              scrollbarWidth: "thin",
              scrollbarColor: `${scrollThumbBg} ${scrollBg}`
            }}
          >
            {!isSignedIn ? (
              <Box p={6} textAlign="center">
                <Text color="gray.500" mb={4}>
                  Members
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Sign in to add, edit, and manage your family members
                </Text>
              </Box>
            ) : filteredMembers.length > 0 ? (
              filteredMembers.map((member, index) => {
                const isHighlighted = highlightedMemberId === member.id;
                return (
                  <Box key={member.id}>
                    <Flex
                      py={2}
                      align="center"
                      borderRadius="md"
                      px={2}
                      mx={-2}
                      bg={isHighlighted ? highlightBg : "transparent"}
                      borderWidth={isHighlighted ? "1px" : "0"}
                      borderColor={isHighlighted ? highlightBorder : "transparent"}
                      pointerEvents={isOpen && !isHighlighted ? "none" : "auto"}
                      opacity={isOpen && !isHighlighted ? 0.5 : 1}
                      transition="all 0.2s"
                      _hover={{
                        bg: isOpen ? (isHighlighted ? highlightBg : "transparent") : hoverBg
                      }}
                    >
                      {/* Member info section - clickable for edit */}
                      <Tooltip label={`View info for ${member.name}`} placement="top" hasArrow isDisabled={isOpen}>
                        <Flex
                          flex="1"
                          align="center"
                          cursor={isOpen ? "default" : "pointer"}
                          onClick={() => !isOpen && handleEditMember(member)}
                        >
                          <Avatar size="sm" name={member.name} src={member.profileImageUrl} mr={2} />
                          <Box flex="1" overflow="hidden">
                            <Text fontWeight="bold" color={textColor} fontSize="sm" noOfLines={1}>
                              {member.name}
                            </Text>
                            <Flex align="center" mt={0.5}>
                              <Text fontSize="xs" color="gray.500" mr={2} noOfLines={1}>
                                {calculateAge(member.birthdate)} years
                              </Text>
                              <Badge
                                colorScheme={member.gender === "M" ? "blue" : member.gender === "F" ? "pink" : "purple"}
                                fontSize="2xs"
                                px={1}
                              >
                                {member.gender === "M" ? "Male" : member.gender === "F" ? "Female" : "Other"}
                              </Badge>

                              {/* Display role badge if we have group membership info */}
                              {groupMembers.some((gm) => gm.member.id === member.id) && (
                                <Badge ml={2} colorScheme="green" fontSize="2xs" px={1} variant="outline">
                                  {groupMembers.find((gm) => gm.member.id === member.id)?.groupMembership.role ||
                                    "member"}
                                </Badge>
                              )}
                            </Flex>
                          </Box>
                        </Flex>
                      </Tooltip>

                      {/* Three dots menu */}
                      <Box>
                        <Menu placement="bottom-end" isLazy isOpen={isOpen && isHighlighted ? false : undefined}>
                          <MenuButton
                            as={IconButton}
                            icon={<FaEllipsisV />}
                            variant="ghost"
                            size="sm"
                            aria-label="More options"
                            isDisabled={isOpen}
                            _hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
                          />
                          <MenuList
                            fontSize="sm"
                            py={1}
                            boxShadow="md"
                            borderColor={useColorModeValue("gray.200", "gray.700")}
                          >
                            <MenuItem
                              icon={<FaEye />}
                              _hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
                              onClick={() => handleEditMember(member)}
                            >
                              View
                            </MenuItem>
                            <MenuItem
                              icon={<FaPen />}
                              _hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
                              onClick={() => handleEditMember(member)}
                            >
                              Edit
                            </MenuItem>

                            {/* Admin Toggle Option - Only show if we have group membership info */}
                            {groupMembers.some((gm) => gm.member.id === member.id) && (
                              <MenuItem
                                icon={<FaUserShield />}
                                _hover={{ bg: useColorModeValue("blue.50", "blue.900") }}
                                color="blue.500"
                                onClick={(e) => handleToggleAdminStatus(member, e)}
                              >
                                {groupMembers.find((gm) => gm.member.id === member.id)?.groupMembership.role === "admin"
                                  ? "Remove Admin Status"
                                  : "Make Admin"}
                              </MenuItem>
                            )}

                            <Divider my={1} />
                            <MenuItem
                              icon={<FaTrash />}
                              color="red.500"
                              _hover={{
                                bg: useColorModeValue("red.50", "red.900"),
                                color: useColorModeValue("red.700", "red.200")
                              }}
                              onClick={(e) => handleDeleteClick(member, e)}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Box>
                    </Flex>
                    {index < filteredMembers.length - 1 && <Divider />}
                  </Box>
                );
              })
            ) : (
              <Box p={4} textAlign="center">
                {searchTerm ? (
                  <Text color="gray.500">No members match your search</Text>
                ) : error ? (
                  <Text color={errorTextColor}>{error}</Text>
                ) : noDefaultGroup ? (
                  <Box>
                    <Text color="gray.500" mb={2}>
                      You don't have a default group set
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                      Create a group or select a default group in your settings to see members here
                    </Text>
                  </Box>
                ) : (
                  <Text color="gray.500">
                    {members.length === 0
                      ? "No members in your default group. Add some members to get started."
                      : "No members found"}
                  </Text>
                )}
              </Box>
            )}
          </Box>
        </CardBody>
      </Card>

      {/* Delete Confirmation Alert Dialog */}
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={handleCloseDialog}
        isOpen={isOpen}
        isCentered
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Member
            </AlertDialogHeader>
            <AlertDialogCloseButton onClick={handleCloseDialog} />
            <AlertDialogBody>
              <Text mb={4}>Are you sure you want to delete this member?</Text>
              <Flex justify="center" my={6}>
                <Text color="red.500" fontSize="2xl" fontWeight="bold" letterSpacing="wide">
                  {memberToDelete?.name}
                </Text>
              </Flex>

              <Text mb={4} color="red.500" fontWeight="medium">
                This action cannot be undone.
              </Text>

              {deleteError && (
                <Text mb={4} color="red.500" fontWeight="bold" p={2} bg="red.50" borderRadius="md">
                  Error: {deleteError}
                </Text>
              )}

              <Box mt={6}>
                <Text mb={2} fontWeight="medium">
                  For security, please type{" "}
                  <Text as="span" fontWeight="bold" color="red.500">
                    {memberToDelete?.name}
                  </Text>{" "}
                  to confirm deletion:
                </Text>
                <FormControl isInvalid={confirmationText.length > 0 && !isConfirmationValid}>
                  <Input
                    ref={confirmInputRef}
                    placeholder={memberToDelete?.name || ""}
                    value={confirmationText}
                    onChange={handleConfirmationTextChange}
                    autoComplete="off"
                    borderColor={confirmationText.length > 0 && !isConfirmationValid ? "red.300" : undefined}
                  />
                  {confirmationText.length > 0 && !isConfirmationValid && (
                    <FormErrorMessage>Please type the exact name "{memberToDelete?.name}" to continue</FormErrorMessage>
                  )}
                </FormControl>
              </Box>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCloseDialog} isDisabled={isDeleting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={handleConfirmDelete}
                isDisabled={!isConfirmationValid || isDeleting}
                opacity={isConfirmationValid && !isDeleting ? 1 : 0.6}
                _hover={isConfirmationValid && !isDeleting ? { bg: "red.600" } : undefined}
                isLoading={isDeleting}
                loadingText="Deleting..."
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default MembersCard;
